import { ensureWorkoutDraftHydrated } from '@/lib/fitnessDraftPersistence';
import { MIRROR_FITNESS_CARDS_ENABLED } from '@/lib/featureFlags';
import { fitnessWorkoutController, getFitnessWorkoutRuntime, getSavedPlanPrepareSlot, isSavedPlanPrepareSlotCurrent, type FitnessWorkoutState } from '@/lib/fitnessWorkoutController';
import { materializeFitnessSavedPlanDay, type FitnessSavedPlanMaterialization } from '@/lib/fitnessSavedPlanPrepareCodec';
import { fitnessLocalWorkoutId } from '../../../supabase/functions/_shared/fitnessLocalWorkoutDomain';
import { canonicalFitnessSavedPlanPrepareEnvelope, type FitnessSavedPlanPrepareEnvelope, type FitnessSavedPlanPrepareSelection } from '../../../supabase/functions/_shared/fitnessSavedPlanPrepareDomain';
import { parseFitnessSavedPlanPrepareRequest, normalizeFitnessSavedPlanPrepareName } from '../../../supabase/functions/_shared/fitnessSavedPlanPrepareIntent';
import type { FitnessRuntimeAuth } from '@/lib/fitnessRuntimeAction';

interface QueryResult { data: unknown; error: unknown }
export interface FitnessSavedPlanPrepareQuery extends PromiseLike<QueryResult> {
  eq(column: string, value: string): FitnessSavedPlanPrepareQuery;
  limit(value: number): FitnessSavedPlanPrepareQuery;
  setHeader(name: string, value: string): FitnessSavedPlanPrepareQuery;
}
export interface FitnessSavedPlanPrepareBindings { from(table: 'workout_plans'): { select(columns: string): FitnessSavedPlanPrepareQuery } }
export interface FitnessSavedPlanPrepareRequestOptions {
  ownerId: string; text: string; auth: FitnessRuntimeAuth; bindings: FitnessSavedPlanPrepareBindings; isCurrent: () => boolean; now?: () => number;
}
type Slot = ReturnType<typeof getSavedPlanPrepareSlot>;
/** Issued objects and closures retain the original request and owned row. A
 * copied projection never supplies installation authority. */
export interface FitnessSavedPlanPrepareRequestBinding {
  readonly ownerId: string; readonly slot: Slot | null; readonly materialization: FitnessSavedPlanMaterialization | null;
  readonly assertCurrent: () => void; readonly assertOrigin: () => void;
  readonly revalidate: (token: string, guard: () => void) => Promise<void>;
}
interface PrivateIdentity { ownerId: string | null; epoch: number; runId: string | null; lifecycle: number | null; revision: number | null; paused: boolean | null; state: FitnessWorkoutState | null; busy: string | null }
const requests = new WeakMap<FitnessSavedPlanPrepareEnvelope, FitnessSavedPlanPrepareRequestBinding>();
const fail = (code: string): never => { throw new Error(code); };
const nowOf = (options: FitnessSavedPlanPrepareRequestOptions) => options.now?.() ?? Date.now();
const freeze = <T,>(value: T): T => { if (value && typeof value === 'object') { Object.values(value).forEach(freeze); Object.freeze(value); } return value; };
function identityOf(ownerId: string): PrivateIdentity {
  const identity = fitnessWorkoutController.getIdentity(), runtime = getFitnessWorkoutRuntime(ownerId);
  return { ownerId: identity.ownerId, epoch: identity.epoch, runId: runtime?.runId ?? null, lifecycle: runtime?.lifecycle ?? null, revision: runtime?.revision ?? null, paused: runtime?.paused ?? null, state: runtime?.state ?? null, busy: runtime?.busy ?? null };
}
function sameIdentity(a: PrivateIdentity, b: PrivateIdentity): boolean {
  return a.ownerId === b.ownerId && a.epoch === b.epoch && a.runId === b.runId && a.lifecycle === b.lifecycle && a.revision === b.revision && a.paused === b.paused && a.state === b.state && a.busy === b.busy;
}
function rowKey(row: unknown): string {
  const value = JSON.stringify(row, (_key, item: unknown) => {
    if (!item || typeof item !== 'object' || Array.isArray(item)) return item;
    if (![Object.prototype, null].includes(Object.getPrototypeOf(item))) fail('fitness_saved_plan_prepare_changed');
    const sorted: Record<string, unknown> = Object.create(null);
    for (const key of Object.keys(item).sort()) {
      if (['__proto__', 'constructor', 'prototype'].includes(key)) fail('fitness_saved_plan_prepare_changed');
      sorted[key] = (item as Record<string, unknown>)[key];
    }
    return sorted;
  });
  if (!value || new TextEncoder().encode(value).length > 512 * 1024) fail('fitness_saved_plan_prepare_changed');
  return value;
}
async function readOwnedPlan(options: FitnessSavedPlanPrepareRequestOptions, selection: FitnessSavedPlanPrepareSelection, token: string, guard: () => void): Promise<{ row: unknown; materialization: FitnessSavedPlanMaterialization; key: string }> {
  guard();
  let id = selection.plan;
  if (!fitnessLocalWorkoutId(id)) {
    // A complete bounded owner index proves uniqueness even when PostgreSQL's
    // text comparison treats canonically equivalent Unicode differently.
    const index = await options.bindings.from('workout_plans').select('id,user_id,name').eq('user_id', options.ownerId).limit(257).setHeader('Authorization', 'Bearer ' + token); guard();
    const indexData = index.data;
    if (index.error || !Array.isArray(indexData) || indexData.length > 256 || Reflect.ownKeys(indexData).length !== indexData.length + 1) return fail('fitness_saved_plan_prepare_unavailable');
    const rows: unknown[] = structuredClone(indexData); guard();
    const ids = new Set<string>(), matches: string[] = [];
    for (const value of rows) {
      if (!value || typeof value !== 'object' || Array.isArray(value) || Reflect.ownKeys(value).length !== 3) fail('fitness_saved_plan_prepare_unavailable');
      const row = value as Record<string, unknown>;
      const rowId = row.id, rowName = row.name;
      if (!['id', 'user_id', 'name'].every(key => Object.prototype.hasOwnProperty.call(row, key)) || !fitnessLocalWorkoutId(rowId) || rowId !== rowId.toLowerCase()
        || row.user_id !== options.ownerId || typeof rowName !== 'string' || !rowName.trim() || rowName.length > 160 || /[\p{Cc}\p{Cf}\p{Cs}]/u.test(rowName) || ids.has(rowId)) return fail('fitness_saved_plan_prepare_unavailable');
      ids.add(rowId);
      if (normalizeFitnessSavedPlanPrepareName(rowName) === normalizeFitnessSavedPlanPrepareName(selection.plan)) matches.push(rowId);
    }
    if (matches.length !== 1) fail('fitness_saved_plan_prepare_unavailable');
    id = matches[0]; guard();
  }
  const response = await options.bindings.from('workout_plans').select('id,user_id,name,days').eq('user_id', options.ownerId).eq('id', id.toLowerCase()).limit(2).setHeader('Authorization', 'Bearer ' + token); guard();
  if (response.error || !Array.isArray(response.data) || response.data.length !== 1) fail('fitness_saved_plan_prepare_unavailable');
  const row: unknown = structuredClone(response.data[0]); guard();
  if (!row || typeof row !== 'object' || Array.isArray(row) || (row as Record<string, unknown>).id !== id.toLowerCase()) fail('fitness_saved_plan_prepare_unavailable');
  const materialization = materializeFitnessSavedPlanDay(row, selection, options.ownerId), key = rowKey(row); guard();
  return { row, materialization, key };
}

export async function readFitnessSavedPlanPrepareForRequest(input: FitnessSavedPlanPrepareRequestOptions): Promise<FitnessSavedPlanPrepareEnvelope | null> {
  const selection = parseFitnessSavedPlanPrepareRequest(input.text);
  if (!selection || !MIRROR_FITNESS_CARDS_ENABLED) return null;
  const options = { ...input, auth: { getUser: input.auth.getUser.bind(input.auth), getSession: input.auth.getSession.bind(input.auth) }, bindings: { from: input.bindings.from.bind(input.bindings) } };
  const assertOrigin = () => { if (!fitnessLocalWorkoutId(options.ownerId) || options.ownerId !== options.ownerId.toLowerCase() || !options.isCurrent()) fail('invalid_session'); };
  assertOrigin(); const user = await options.auth.getUser(); assertOrigin();
  if (user.error || user.data.user?.id !== options.ownerId) fail('invalid_session');
  const session = await options.auth.getSession(); assertOrigin();
  if (session.error || session.data.session?.user.id !== options.ownerId || !session.data.session.access_token) fail('invalid_session');
  // A newly created WebView may have no session cache but an acknowledged run.
  // Hydrate before an empty slot can authorize preparing another workout.
  let hydrated = false;
  try { await ensureWorkoutDraftHydrated(options.ownerId, () => { assertOrigin(); return true; }); hydrated = true; }
  catch { assertOrigin(); /* A failed read cannot establish an empty preparation slot. */ }
  assertOrigin();
  let slot: Slot | null = null;
  if (hydrated) { try { slot = getSavedPlanPrepareSlot(options.ownerId); } catch { /* An occupied or unreadable slot cannot authorize a new workout. */ } }
  const identity = identityOf(options.ownerId), target = slot;
  const assertCurrent = () => {
    assertOrigin();
    if (!sameIdentity(identity, identityOf(options.ownerId)) || target && !isSavedPlanPrepareSlotCurrent(target)) fail('fitness_saved_plan_prepare_changed');
    assertOrigin();
    if (!sameIdentity(identity, identityOf(options.ownerId))) fail('fitness_saved_plan_prepare_changed');
  };
  assertCurrent();
  let materialization: FitnessSavedPlanMaterialization | null = null, originalKey: string | null = null;
  if (target) {
    try {
      const result = await readOwnedPlan(options, selection, session.data.session.access_token, assertCurrent);
      materialization = result.materialization; originalKey = result.key;
    } catch (cause) {
      assertCurrent();
      if (cause instanceof Error && cause.message === 'invalid_session') throw cause;
    }
  }
  const observedAt = nowOf(options); assertCurrent();
  const envelope = canonicalFitnessSavedPlanPrepareEnvelope({ version: 1, owner: options.ownerId, observedAt: new Date(observedAt).toISOString(), status: materialization ? 'ready' : 'unavailable', preparation: materialization?.preparation ?? null }, options.ownerId, new Date(nowOf(options)));
  const revalidate = async (token: string, guard: () => void) => {
    guard(); assertCurrent();
    if (!target || !materialization || originalKey === null) fail('review_required');
    const result = await readOwnedPlan(options, selection, token, () => { guard(); assertCurrent(); });
    if (result.key !== originalKey || JSON.stringify(result.materialization) !== JSON.stringify(materialization)) fail('fitness_saved_plan_prepare_changed');
    guard(); assertCurrent();
  };
  assertCurrent(); requests.set(envelope, Object.freeze({ ownerId: options.ownerId, slot: target, materialization, assertCurrent, assertOrigin, revalidate })); return freeze(envelope);
}
export function getFitnessSavedPlanPrepareRequestBinding(request: FitnessSavedPlanPrepareEnvelope, ownerId: string): FitnessSavedPlanPrepareRequestBinding | null {
  const binding = requests.get(request); return binding?.ownerId === ownerId ? binding : null;
}
export function isFitnessSavedPlanPrepareRequestCurrent(request: FitnessSavedPlanPrepareEnvelope): boolean {
  const binding = requests.get(request); if (!binding) return false;
  try { binding.assertCurrent(); return true; } catch { return false; }
}
